import type { TableProps } from "../../../../utils/local-antd";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraTable } from "../../../../components/table/AtiraTable";
import { AtriaTourClasses } from "../../../../components/tour/AtiraTourClasses.enum";
import { Entry } from "../../../../model/entry/Entry";
import { WidgetKind } from "../../../../model/widget/types/WidgetKid.enum";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import { entryActions } from "../../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { createEntriesWidgetTableColumns } from "../../../entries/utils/createEntriesWidgetTableColumns";
import { permissionSliceSelectors } from "../../../../redux/permission/permission.selector";
import { permissionActions } from "../../../../redux/permission/permission.slice";
import { TestIDs } from "../../../../tests/types/testIds";
import { EntityIcons } from "../../../../utils/GlobalIcons";
import { DashboardPanel } from "../DashboardPanel";

const StyledTableWrapper = styled.div`
  .ant-table-tbody > tr > td.lead-cell {
    background-color: ${({ theme }) => theme.lead} !important;
    color: ${({ theme }) => theme.onLead} !important;
  }

  .ant-table-tbody > tr > td.contact-cell {
    background-color: ${({ theme }) => theme.contact} !important;
    color: ${({ theme }) => theme.onContact} !important;
  }

  .ant-table-tbody > tr > td.junk-cell {
    background-color: ${({ theme }) => theme.junk} !important;
    color: ${({ theme }) => theme.onJunk} !important;
  }
`;

export const WidgetLatestEntries: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const entries = useAppSelector(entrySliceSelectors.selectLatestEntries);
  const entriesLoading = useAppSelector(
    entrySliceSelectors.selectLatestEntriesLoading,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const tableColumns = useMemo(
    () =>
      createEntriesWidgetTableColumns(
        tableSettings?.columns || [],
        { t },
        members,
      ) as TableProps<Entry>["columns"],

    [tableSettings?.columns, t, members],
  );

  useEffect(() => {
    dispatch(
      entryActions.getLatestEntries({
        userId,
        meta: { count: entriesPageSize, page: 0 },
      }),
    );
    dispatch(entryActions.getUserTableSettings({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
  }, [userId, dispatch, entriesPageSize]);

  return (
    <DashboardPanel
      title={t("dashboard.widget.title.New Entries")}
      cta={{
        to: "/entries",
        label: t("dashboard.widget.btn"),
        icon: EntityIcons.Entries,
        testId: TestIDs.dashboard.widgets[WidgetKind.LAST_ENTRIES].cta,
      }}
      dataTestId={TestIDs.dashboard.widgets[WidgetKind.LAST_ENTRIES].wrapper}
    >
      <StyledTableWrapper className={AtriaTourClasses.WIDGET_LATEST_ENTRIES}>
        <AtiraTable
          columns={tableColumns}
          // We fetch entries based on the page size defined in the settings, but we only want to show 5 entries in the widget, so we slice the data here
          // Reason for this is that if we fetch 5 here, the main table will have 5 rows only instead of the defined page size by the user
          data={entries.slice(0, 5)}
          loading={entriesLoading && !entries.length}
          rowSelection={undefined}
          pagination={false}
        />
      </StyledTableWrapper>
    </DashboardPanel>
  );
};
